import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Chip,
  Checkbox,
  FormControlLabel,
  Alert,
  CircularProgress,
} from '@mui/material';
import { ArrowForward as ArrowForwardIcon } from '@mui/icons-material';
import config from '../../config';
import { authFetch, handleApiResponse } from '../../api/authFetch';
import { describeAdminError } from '../../utils/adminErrors';
import { adminColors, radius, TOUCH_HIT_SX } from '../../theme/adminTheme';

// Phase 4 (admin console v2) — the "smart transfer check" the client's
// mockups showed for StudentDrawer's move-school action: instead of
// AccountSection's SchoolPicker committing a school change the instant it's
// picked (the pre-Phase-4 behavior everywhere else this control appears —
// see AccountSection.js/StaffDrawer.js, both unchanged), StudentDrawer
// routes a picked target through this dialog first, which previews exactly
// which class enrolments would be dropped vs. kept via
// POST /users/{id}/transfer-check before anything is written, then passes
// the admin's `dropEnrollments` choice on to the real
// PUT /users/{id}/school call.
//
// Deliberately scoped to StudentDrawer only (see that file's own comment) —
// AccountSection/StaffDrawer's school-change flow is untouched, so a staff
// member's school still changes immediately on pick, exactly as before.
//
// Safe-by-default: `dropEnrollments` starts unchecked. A school change with
// the box left unchecked is byte-identical to the pre-Phase-4 behavior
// (school field moves, enrolments untouched) — dropping enrolments is
// something the admin must actively opt into, with the exact list of what
// that opt-in removes shown right above the checkbox.
export default function TransferSchoolDialog({
  open,
  onClose,
  userId,
  username,
  currentSchoolName,
  targetSchoolName,
  // Fallback context only — the student's own current classes (same shape
  // GET /users/{id}/groups already returns), used to describe what a
  // `dropEnrollments` opt-in would remove on the rare path where the target
  // school has no backing `School` row to preview against (see
  // "unavailable" phase below).
  currentEnrollments,
  // (dropEnrollments: boolean) => Promise<boolean> — resolves `true` on a
  // successful move (this dialog is closed by the caller once true comes
  // back) or a falsy value on failure (the caller has already toasted the
  // error; this dialog just stays open so the admin can retry or cancel).
  onConfirmed,
}) {
  const navigate = useNavigate();
  const [phase, setPhase] = useState('loading'); // 'loading' | 'preview' | 'unavailable' | 'error'
  const [preview, setPreview] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [dropEnrollments, setDropEnrollments] = useState(false);
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    setDropEnrollments(false);
    setConfirming(false);
    setPreview(null);
    setLoadError('');
    setPhase('loading');

    const controller = new AbortController();
    (async () => {
      try {
        const schoolsRes = await authFetch(`${config.API_BASE_URL}/api/admin/schools`, {}, { signal: controller.signal });
        const schoolsData = await handleApiResponse(schoolsRes, navigate);
        if (schoolsData === null || controller.signal.aborted) return; // 401 — already redirected
        const targetNormalized = (targetSchoolName || '').trim().toLowerCase();
        const match = (schoolsData.schools || []).find(
          (s) => (s.name || '').trim().toLowerCase() === targetNormalized
        );
        if (!match || match.id == null) {
          // No backing `School` row for this name (e.g. clearing to
          // "Unassigned", or a school that only ever existed as a
          // Group.school string — see SchoolManagement.js's own comment on
          // `possible_duplicate_of`/synthesized rows) — transfer-check
          // requires a real `school_id`, so there's nothing to preview.
          // The move itself still works via the fallback below.
          setPhase('unavailable');
          return;
        }
        const checkRes = await authFetch(`${config.API_BASE_URL}/api/users/${userId}/transfer-check`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ school_id: match.id }),
        }, { signal: controller.signal });
        const data = await handleApiResponse(checkRes, navigate);
        if (data === null || controller.signal.aborted) return; // 401 — already redirected
        setPreview(data);
        setPhase('preview');
      } catch (err) {
        if (err?.name === 'AbortError' || controller.signal.aborted) return;
        setLoadError(describeAdminError(err, "Couldn't preview this move."));
        setPhase('error');
      }
    })();
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- username/currentEnrollments/onConfirmed are stable for the lifetime of one open dialog (same student, same underlying handlers)
  }, [open, targetSchoolName, userId]);

  const handleConfirm = async () => {
    setConfirming(true);
    const ok = await onConfirmed(dropEnrollments);
    setConfirming(false);
    if (ok) onClose();
  };

  const targetLabel = targetSchoolName || 'Unassigned';
  const wouldDrop = preview?.would_drop_enrollments || [];
  const kept = preview?.kept_enrollments || [];
  const fallbackCount = (currentEnrollments || []).length;

  return (
    <Dialog open={open} onClose={confirming ? undefined : onClose} maxWidth="xs" fullWidth aria-labelledby="transfer-school-title">
      <DialogTitle id="transfer-school-title" sx={{ fontWeight: 700 }}>
        Move {username} to a new school
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5, flexWrap: 'wrap' }}>
          <Chip label={currentSchoolName || 'Unassigned'} size="small" sx={{ fontWeight: 600, bgcolor: adminColors.surface }} />
          <ArrowForwardIcon sx={{ fontSize: 16, color: adminColors.inkSoft }} />
          <Chip label={targetLabel} size="small" sx={{ fontWeight: 700, bgcolor: adminColors.accentLight, color: adminColors.accentDark }} />
        </Box>

        {phase === 'loading' && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={22} />
          </Box>
        )}

        {phase === 'error' && (
          <Alert severity="warning" variant="outlined" sx={{ mb: 2 }}>
            {loadError} Moving will still work — check {username}&rsquo;s classes afterward to confirm nothing was
            left behind.
          </Alert>
        )}

        {phase === 'unavailable' && (
          <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
            There&rsquo;s no school record for &ldquo;{targetLabel}&rdquo; to preview affected classes against. The
            school field will still update — check {username}&rsquo;s classes afterward if needed.
          </Alert>
        )}

        {phase === 'preview' && preview?.no_change && (
          <Alert severity="info" variant="outlined">
            {username} is already at {targetLabel}. Closing this without saving.
          </Alert>
        )}

        {phase === 'preview' && !preview?.no_change && (
          <>
            {kept.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: adminColors.inkSoft, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                  Stays enrolled in
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                  {kept.map((g) => (
                    <Chip key={g.group_id} label={g.group_name} size="small" sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 600 }} />
                  ))}
                </Box>
              </Box>
            )}

            {wouldDrop.length === 0 ? (
              <Typography variant="body2" sx={{ color: adminColors.inkSoft }}>
                No class enrolments will be affected by this move.
              </Typography>
            ) : (
              <Box
                sx={{
                  border: `1px solid ${adminColors.accentBorder}`,
                  borderRadius: radius.control,
                  p: 1.5,
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: 700, color: '#991b1b', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                  At {currentSchoolName || 'the old school'} — {wouldDrop.length} class{wouldDrop.length === 1 ? '' : 'es'}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5, mb: 1 }}>
                  {wouldDrop.map((g) => (
                    <Chip key={g.group_id} label={g.group_name} size="small" sx={{ bgcolor: '#fef2f2', color: '#991b1b', fontWeight: 600 }} />
                  ))}
                </Box>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={dropEnrollments}
                      onChange={(e) => setDropEnrollments(e.target.checked)}
                      size="small"
                      sx={TOUCH_HIT_SX}
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ color: adminColors.ink }}>
                      Also remove {username} from {wouldDrop.length === 1 ? 'this class' : 'these classes'}
                    </Typography>
                  }
                />
                {dropEnrollments && (
                  <Alert severity="warning" variant="outlined" sx={{ mt: 1 }}>
                    This can&rsquo;t be undone — {username} would need to be re-added manually if this was a
                    mistake.
                  </Alert>
                )}
                {!dropEnrollments && (
                  <Typography variant="caption" sx={{ display: 'block', color: adminColors.inkSoft, mt: 1 }}>
                    Left unchecked, {username} stays enrolled in {wouldDrop.length === 1 ? 'this class' : 'these classes'} at{' '}
                    {currentSchoolName || 'the old school'} even after the move.
                  </Typography>
                )}
              </Box>
            )}
          </>
        )}

        {(phase === 'unavailable' || phase === 'error') && fallbackCount > 0 && (
          <FormControlLabel
            control={
              <Checkbox checked={dropEnrollments} onChange={(e) => setDropEnrollments(e.target.checked)} size="small" sx={TOUCH_HIT_SX} />
            }
            label={
              <Typography variant="body2" sx={{ color: adminColors.ink }}>
                Also remove {username} from {fallbackCount} class{fallbackCount === 1 ? '' : 'es'} at{' '}
                {currentSchoolName || 'the old school'}
              </Typography>
            }
          />
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} disabled={confirming}>
          Cancel
        </Button>
        {!(phase === 'preview' && preview?.no_change) && (
          <Button
            variant="contained"
            onClick={handleConfirm}
            disabled={phase === 'loading' || confirming}
            sx={{ ...TOUCH_HIT_SX, backgroundColor: adminColors.accent, '&:hover': { backgroundColor: adminColors.accentDark } }}
          >
            {confirming && <CircularProgress size={16} sx={{ mr: 1, color: 'white' }} />}
            Move to {targetLabel}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
